import React, { useRef, useEffect } from "react";
import { resolveUrl, useAdmin } from "./adminShared";
import { Button } from "@/components/ui/button";
import { RotateCcw, Move } from "lucide-react";

// Reference design size of the Visualizer screen (matches Visualizer.js DESIGN_W/H)
const DESIGN_H = 1080;

// Approximate top-left percentage for each preset anchor (used as starting point
// before the user drags). The user fine-tunes by dragging afterwards.
const PRESET_XY = {
  "top-left": { x: 2, y: 3 },
  "top-right": { x: 80, y: 3 },
  "top-center": { x: 44, y: 3 },
  "bottom-left": { x: 2, y: 78 },
  "bottom-right": { x: 80, y: 78 },
};
const presetXY = (p) => PRESET_XY[p] || { x: 2, y: 3 };

export const PlacementEditor = () => {
  const ctx = useAdmin();
  const {
    logoUrl, logoSize, setLogoSize, logoZIndex, logoOpacity, logoPosition, logoX, setLogoX, logoY, setLogoY,
    extraImage1Url, extraImage1Size, setExtraImage1Size, extraImage1ZIndex, extraImage1Opacity, extraImage1Position, extraImage1X, setExtraImage1X, extraImage1Y, setExtraImage1Y,
    extraImage2Url, extraImage2Size, setExtraImage2Size, extraImage2ZIndex, extraImage2Opacity, extraImage2Position, extraImage2X, setExtraImage2X, extraImage2Y, setExtraImage2Y,
    extraImage3Url, extraImage3Size, setExtraImage3Size, extraImage3ZIndex, extraImage3Opacity, extraImage3Position, extraImage3X, setExtraImage3X, extraImage3Y, setExtraImage3Y,
    textTitle,
  } = ctx;

  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const items = [
    { key: "logo", label: "Logo", url: logoUrl, size: logoSize, setSize: setLogoSize, z: logoZIndex, op: logoOpacity, preset: logoPosition, x: logoX, y: logoY, setX: setLogoX, setY: setLogoY },
    { key: "img1", label: "Imagen 1", url: extraImage1Url, size: extraImage1Size, setSize: setExtraImage1Size, z: extraImage1ZIndex, op: extraImage1Opacity, preset: extraImage1Position, x: extraImage1X, y: extraImage1Y, setX: setExtraImage1X, setY: setExtraImage1Y },
    { key: "img2", label: "Imagen 2", url: extraImage2Url, size: extraImage2Size, setSize: setExtraImage2Size, z: extraImage2ZIndex, op: extraImage2Opacity, preset: extraImage2Position, x: extraImage2X, y: extraImage2Y, setX: setExtraImage2X, setY: setExtraImage2Y },
    { key: "img3", label: "Imagen 3", url: extraImage3Url, size: extraImage3Size, setSize: setExtraImage3Size, z: extraImage3ZIndex, op: extraImage3Opacity, preset: extraImage3Position, x: extraImage3X, y: extraImage3Y, setX: setExtraImage3X, setY: setExtraImage3Y },
  ].filter((it) => it.url && it.url.trim() !== "");

  const getXY = (it) => ({
    x: it.x != null ? it.x : presetXY(it.preset).x,
    y: it.y != null ? it.y : presetXY(it.preset).y,
  });

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      if (d.type === "move") {
        let xpct = ((e.clientX - rect.left) / rect.width) * 100 - d.offsetXpct;
        let ypct = ((e.clientY - rect.top) / rect.height) * 100 - d.offsetYpct;
        xpct = Math.max(0, Math.min(98, xpct));
        ypct = Math.max(0, Math.min(98, ypct));
        d.setX(Math.round(xpct * 10) / 10);
        d.setY(Math.round(ypct * 10) / 10);
      } else if (d.type === "resize") {
        const scale = rect.height / DESIGN_H;
        let newSize = d.startSize + (e.clientY - d.startClientY) / scale;
        newSize = Math.max(20, Math.min(600, Math.round(newSize)));
        d.setSize(newSize);
      }
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const startMove = (e, it) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const { x, y } = getXY(it);
    const cursorXpct = ((e.clientX - rect.left) / rect.width) * 100;
    const cursorYpct = ((e.clientY - rect.top) / rect.height) * 100;
    dragRef.current = { type: "move", setX: it.setX, setY: it.setY, offsetXpct: cursorXpct - x, offsetYpct: cursorYpct - y };
  };

  const startResize = (e, it) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { type: "resize", setSize: it.setSize, startSize: it.size, startClientY: e.clientY };
  };

  return (
    <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700" data-testid="placement-editor">
      <div className="flex items-center gap-2 mb-1">
        <Move className="w-4 h-4 text-pink-400" />
        <span className="text-zinc-200 font-semibold">Colocación visual (logo e imágenes)</span>
      </div>
      <p className="text-zinc-500 text-xs mb-3">
        Arrastra cada elemento para moverlo y tira de la esquina inferior derecha para cambiar su tamaño. Esta vista representa la pantalla del Visualizer (16:9). Recuerda pulsar &quot;Guardar configuración&quot; al terminar.
      </p>

      {items.length === 0 ? (
        <p className="text-zinc-500 text-sm py-6 text-center">Sube un logo o imágenes extra para colocarlos aquí.</p>
      ) : (
        <>
          <div
            ref={canvasRef}
            className="relative w-full aspect-video rounded-lg overflow-hidden border border-zinc-600 select-none touch-none"
            style={{ background: "#09090B" }}
            data-testid="placement-canvas"
          >
            {/* Faint reference of the scoreboard so the user knows where things land */}
            <div className="absolute inset-0 opacity-[0.18] pointer-events-none">
              <div className="absolute top-[6%] left-1/2 -translate-x-1/2 text-white font-black text-[3vw] tracking-tight whitespace-nowrap">{textTitle || "CLASIFICACIÓN"}</div>
              <div className="absolute left-[3%] top-[22%] bottom-[6%] w-[28%] rounded bg-white/10 border border-white/10" />
              <div className="absolute right-[3%] top-[22%] bottom-[6%] w-[28%] rounded bg-white/10 border border-white/10" />
            </div>

            {items.map((it) => {
              const { x, y } = getXY(it);
              const heightPct = (it.size / DESIGN_H) * 100;
              return (
                <div
                  key={it.key}
                  onPointerDown={(e) => startMove(e, it)}
                  className="absolute group"
                  style={{ left: `${x}%`, top: `${y}%`, height: `${heightPct}%`, zIndex: it.z, opacity: (it.op ?? 100) / 100, cursor: "move" }}
                  data-testid={`placement-item-${it.key}`}
                >
                  <img
                    src={resolveUrl(it.url)}
                    alt={it.label}
                    draggable={false}
                    className="h-full w-auto object-contain drop-shadow-2xl pointer-events-none ring-1 ring-pink-500/0 group-hover:ring-pink-500/70 rounded-sm"
                    onError={(e) => (e.target.style.opacity = 0.2)}
                  />
                  <span className="absolute -top-4 left-0 text-[10px] text-pink-300 font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">{it.label}</span>
                  {/* Resize handle */}
                  <div
                    onPointerDown={(e) => startResize(e, it)}
                    className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-pink-500 border-2 border-white cursor-nwse-resize shadow"
                    title="Arrastra para redimensionar"
                    data-testid={`placement-resize-${it.key}`}
                  />
                </div>
              );
            })}
          </div>

          {/* Per-element controls / reset */}
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {items.map((it) => {
              const isFree = it.x != null && it.y != null;
              return (
                <div key={it.key} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-zinc-900/60 border border-zinc-700">
                  <span className="text-zinc-300 text-sm">{it.label} <span className="text-zinc-500 text-xs">({it.size}px)</span></span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!isFree}
                    onClick={() => { it.setX(null); it.setY(null); }}
                    className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40"
                    data-testid={`placement-reset-${it.key}`}
                    title="Volver a la posición predefinida"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    {isFree ? "Posición libre" : "Predefinida"}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
